import {
  createFileRoute,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import UpdateProfileModalContent from 'app/_authenticated/funds/withdraw/components/UpdateProfileModalContent/UpdateProfileModalContent';
import Modal from 'components/Modal/Modal';
import VerifyOtp from 'components/Otp/VerifyOtp/VerifyOtp';
import { useProduct } from 'hooks/products/useProducts';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useOtpStatus from 'hooks/useOtpStatus';
import useResetOtpTimeout from 'hooks/useResetOtpTimeout';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/reducer';
import { useCreateOrUpdateCryptoAccountMutation } from 'store/slices/crypto-accounts-api';
import { useGetProfileQuery } from 'store/slices/profile-api';
import { otpProcesses } from 'utils/constants';
import { z } from 'zod';
import messages from '../../../messages';
import CryptoWalletTwoFactor from './components/CryptoWalletTwoFactor';

const searchParamsSchema = z.object({
  accountId: z.string().optional(),
  address: z.string(),
  isHosted: z.boolean().optional(),
  institutionId: z.string().optional(),
  name: z.string().optional(),
});
export const Route = createFileRoute(
  '/_authenticated/funds/withdraw/crypto/$productCode/wallet/edit/otp',
)({
  component: CryptoWalletView,
  validateSearch: zodValidator(searchParamsSchema),
});

function CryptoWalletView() {
  const { productCode } = Route.useParams();
  const { product } = useProduct(productCode);
  const { accountId, address, isHosted, institutionId, name } =
    Route.useSearch();

  const otpFlowTypeCode = `PROFILE_${productCode}_WALLET`;

  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCompleteEmailTfaModal, setShowCompleteEmailTfaModal] =
    useState(false);

  const { language } = useSelector(
    (rootState: RootState) => rootState.language,
  );

  const [createOrUpdateCryptoAccount, createOrUpdateCryptoAccountResult] =
    useCreateOrUpdateCryptoAccountMutation();

  const otpStatus = useOtpStatus();
  const { data: userProfileResponse } = useGetProfileQuery();
  const resetTimestamp = useResetOtpTimeout();
  const navigate = useNavigate();
  const router = useRouter();

  const userEmail = userProfileResponse?.email;
  const twoFactorMessages = messages[language].TwoFactorForm;
  const otpMessages = messages[language].Otp;

  const closeModal = () => {
    navigate({ to: '/settings' });
  };

  async function handleSubmit(twoFaPin: string) {
    var res = await createOrUpdateCryptoAccount({
      otpFlowTypeCode: otpFlowTypeCode,
      TwoFA: twoFaPin,
      wallet: {
        productCode,
        accountId,
        address,
        name: name!,
        isHosted,
        institutionId,
      },
    });

    if (res.error) {
      return;
    }

    if (twoFaPin == '') {
      setShowCompleteEmailTfaModal(true);
    } else {
      setShowCompleteModal(true);
    }

    // Not sure what this is for
    if (otpStatus !== 'TOTP') {
      resetTimestamp(
        otpStatus === 'EMAIL' ? 'EMAIL' : 'SMS',
        otpProcesses.PROFILE_BTC_WALLET,
      );
    }
  }

  const renderForm = () => {
    switch (otpStatus) {
      case 'TOTP':
        return (
          <div>
            <div className="sm:mx-auto sm:w-full sm:max-w-md mb-7">
              <h2 className="font-semibold leading-9 pl-11 pr-11 text-2.5x1  text-center">
                {twoFactorMessages.heading}
              </h2>
              <h6 className="leading-9 pl-11 pr-11 pt-2 text-2x1 text-body-text text-center">
                {twoFactorMessages.subheading}
              </h6>
            </div>
            <CryptoWalletTwoFactor
              onTwoFactorValidated={handleSubmit}
              loading={createOrUpdateCryptoAccountResult.isLoading}
              error={!!createOrUpdateCryptoAccountResult.error}
              errorMessage={createOrUpdateCryptoAccountResult.error?.message}
            />
          </div>
        );
      case 'SMS':
      case 'EMAIL':
        return (
          <div>
            <VerifyOtp
              title={otpMessages.title(product?.name)}
              onChangeOtp={handleSubmit}
              isLoading={createOrUpdateCryptoAccountResult.isLoading}
              error={!!createOrUpdateCryptoAccountResult.error}
              type={otpStatus}
              removeErrorFunction={() => {}}
              process={otpProcesses[otpFlowTypeCode]}
            />
          </div>
        );
    }
  };

  return (
    <>
      <div className="static lg:relative w-full">
        <button
          type="button"
          onClick={() => router.history.back()}
          className="absolute lg:fixed mt-2 ml-6 text-2xl lg:mt-6 lg:ml-6 lg:text-base left-0 top-0 lg:top-auto lg:left-auto z-40 text-header-text focus:outline-none font-semibold">
          <span className="block lg:hidden">←</span>
          <span className="hidden lg:block py-4">
            ← {messages[language].backButton}
          </span>
        </button>

        <div className="flex flex-row justify-start -mb-4 sm:justify-center lg:mb-0 lg:pt-16 xl:pt-16">
          <div className="max-w-1xl mx-auto px-2 lg:px-8">
            <div className="max-w-473 mx-auto">{renderForm()}</div>
            <Modal
              hideModal={() => setShowCompleteModal(false)}
              show={showCompleteModal}
              content={
                <UpdateProfileModalContent
                  closeModal={() => closeModal()}
                  type="twoFA"
                  email={userEmail}
                />
              }
              closeModal={() => closeModal()}
            />
            <Modal
              hideModal={() => setShowCompleteEmailTfaModal(false)}
              show={showCompleteEmailTfaModal}
              content={
                <UpdateProfileModalContent
                  closeModal={() => closeModal()}
                  type="complete"
                />
              }
              closeModal={() => closeModal()}
            />
          </div>
        </div>
      </div>
    </>
  );
}
